import { useQuery, gql } from '@apollo/client';
import { Loader, ExternalLink, FrownIcon, Shield, Clock, AlertTriangle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { buildSparklinkUrl } from '@arthelper/shared/sparklink';
import { useAnalytics } from '~/context/analytics';
import { useEffect } from 'react';
import { DEFAULT_TOWN_SQUARE_URL } from '~/utils/townSquareLinks';

const DMCA_SPARKLINK_URL = buildSparklinkUrl(
  {
    sparkId: '216',
    message: 'Create a DMCA request takedown email for my art',
  },
  { origin: __TOWN_SQUARE_URL__ || DEFAULT_TOWN_SQUARE_URL }
);

// Normalize URLs that start with /search? by prepending Google domain
const normalizeUrl = (url: string): string => {
  if (url.startsWith('/search?') || url.startsWith('/imgres?')) {
    return `https://www.google.com${url}`;
  }
  return url;
};

const LATEST_COPYRIGHT_SCAN = gql`
  query LatestCopyrightScan($imageId: ID!) {
    latestCopyrightScan(imageId: $imageId) {
      id
      status
      results
      createdAt
    }
  }
`;

interface ArtGuardProps {
  imageId: string;
  viewLocation?: 'modal' | 'full_page';
}

export function ArtGuard({ imageId, viewLocation = 'full_page' }: ArtGuardProps) {
  const { t } = useTranslation('artwork');
  const { amplitude } = useAnalytics();
  const { data, loading, error } = useQuery(LATEST_COPYRIGHT_SCAN, {
    variables: { imageId },
    skip: !imageId,
    fetchPolicy: 'network-only',
  });

  const scan = data?.latestCopyrightScan;

  // Track when results are viewed
  useEffect(() => {
    if (scan && !loading && !error) {
      const results = scan.results?.results || [];
      amplitude.track('artguard_results_viewed', {
        image_id: imageId,
        scan_status: scan.status,
        match_count: results.length,
        has_matches: results.length > 0,
        view_location: viewLocation,
      });
    }
  }, [scan, loading, error, imageId, viewLocation, amplitude]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="h-6 w-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center w-full bg-white text-left">
        <div className="text-muted-foreground flex flex-col items-center gap-2 py-20">
          <FrownIcon className="w-10 h-10 text-gray-400" />
          <div>{t('art-guard.error-loading-scan')}</div>
        </div>
      </div>
    );
  }

  // Monitoring status badge component (shown in all states)
  const MonitoringBadge = () => (
    <div className="mb-6 p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border-2 border-[#AF52DE]/20">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <Shield className="w-6 h-6 text-[#AF52DE] mt-0.5" />
          <div className="text-left">
            <h3 className="text-lg font-bold text-gray-900">{t('art-guard.monitoring-title')}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('art-guard.monitoring-description')}</p>
            <p className="text-sm text-gray-600 mt-1">{t('art-guard.recognize-website')}</p>
            <p className="text-sm text-gray-600 mt-1">
              <Trans
                t={t}
                i18nKey="art-guard.unexpected-use"
                components={{
                  link: (
                    <a
                      href={DMCA_SPARKLINK_URL}
                      className="text-purple-500 underline hover:text-purple-600 transition-colors"
                    />
                  ),
                }}
              />
            </p>
          </div>
        </div>
        <div className="relative flex items-center justify-center ml-4 self-center">
          <span className="relative flex h-3 w-3">
            <span
              className="absolute inline-flex h-6 w-6 -top-1.5 -left-1.5 rounded-full bg-[#AF52DE] opacity-40 animate-ping"
              style={{ animationDuration: '2s' }}
            ></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#AF52DE]"></span>
          </span>
        </div>
      </div>
    </div>
  );

  if (!scan) {
    return (
      <div>
        <MonitoringBadge />
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <Shield className="w-10 h-10 text-gray-400" />
          <p className="mt-2">{t('art-guard.no-scan-yet')}</p>
        </div>
      </div>
    );
  }

  const results = scan.results?.results || [];

  switch (scan.status) {
    case 'scheduled':
      return (
        <div>
          <MonitoringBadge />
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Clock className="w-10 h-10 " />
            <p className="mt-2">{t('art-guard.queued-for-scanning')}</p>
          </div>
        </div>
      );

    case 'failed':
      return (
        <div>
          <MonitoringBadge />
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <AlertTriangle className="w-10 h-10 text-red-400" />
            <p className="mt-2">{t('art-guard.scan-failed')}</p>
            <p className="text-sm mt-1">{t('art-guard.working-to-resolve')}</p>
          </div>
        </div>
      );

    case 'completed':
      if (results.length === 0) {
        return (
          <div>
            <MonitoringBadge />
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Shield className="w-10 h-10" />
              <p className="mt-2 ">{t('art-guard.no-matches-found')}</p>
            </div>
          </div>
        );
      }

      return (
        <div className="relative">
          <MonitoringBadge />

          {/* Professional header with scan stats */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-[#AF52DE]" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {t('art-guard.results-found', { count: results.length })}
                </h3>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {results.map((result: any, index: number) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 border-l-2 border-l-[#AF52DE]">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2.5">
                      <span className="inline-flex items-center px-2 py-1 text-xs font-bold bg-[#AF52DE] text-white rounded-md min-w-[28px] justify-center">
                        #{result.resultIndex}
                      </span>
                      <span className="text-sm font-semibold text-gray-900 line-clamp-1">
                        <span className="hidden sm:inline">
                          {result.website.length > 65 ? `${result.website.substring(0, 65)}...` : result.website}
                        </span>
                        <span className="sm:hidden">
                          {result.website.length > 35 ? `${result.website.substring(0, 35)}...` : result.website}
                        </span>
                      </span>
                    </div>
                    <h4
                      className="text-sm font-semibold text-gray-900 mb-2 line-clamp-1 leading-tight"
                      title={result.title}
                    >
                      {result.title}
                    </h4>
                    <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed" title={result.description}>
                      {result.description}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <a
                      href={normalizeUrl(result.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border hover:border-gray-300 rounded-lg transition-all duration-200 hover:shadow-xs"
                      title={`Visit ${result.website}`}
                      onClick={() => {
                        amplitude.track('artguard_match_clicked', {
                          image_id: imageId,
                          scan_id: scan?.id || '',
                          result_index: result.resultIndex,
                          result_url: result.url,
                          total_matches: results.length,
                        });
                      }}
                    >
                      <ExternalLink size={12} className="mr-1.5" />
                      {t('art-guard.view-site')}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Professional footer */}
          <div className="mt-6 p-3 bg-gray-50 rounded-lg border border-gray-200 text-center">
            <p className="text-xs text-gray-600">{t('art-guard.next-scan-footer')}</p>
          </div>
        </div>
      );
  }
}
